import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, AlertTriangle, Calendar, Clock } from "lucide-react";

/**
 * Public accept/deny landing page for open-shift offer emails.
 * Reached via /offer/:token/:action from the email CTA buttons.
 * No auth required — the token is unguessable and single-use per recipient.
 */
export default function OfferResponsePage() {
  const { token, action } = useParams();
  const [offer, setOffer]   = useState(null);
  const [state, setState]   = useState("loading");    // loading | ready | submitting | done | error
  const [result, setResult] = useState(null);         // { status: accepted|denied|already_filled }
  const [error, setError]   = useState("");

  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";

  useEffect(() => {
    let alive = true;
    axios.get(`${apiBase}/open-shift-offer/${token}`)
      .then(({ data }) => { if (alive) { setOffer(data); setState("ready"); } })
      .catch((e) => { if (alive) { setError(e?.response?.data?.detail || "Offer not found or expired."); setState("error"); } });
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [token]);

  const submit = async (chosen) => {
    setState("submitting");
    try {
      const { data } = await axios.post(`${apiBase}/open-shift-offer/${token}/respond?action=${chosen}`);
      setResult(data);
      setState("done");
    } catch (e) {
      setError(e?.response?.data?.detail || "Something went wrong. Please try again.");
      setState("error");
    }
  };

  // If action is baked into the URL, auto-submit on first mount once the offer is loaded.
  useEffect(() => {
    if (state === "ready" && (action === "accept" || action === "deny")) {
      // Small delay so the user briefly sees the shift context before we submit.
      const t = setTimeout(() => submit(action), 250);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line
  }, [state, action]);

  const shell = (children) => (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
        {children}
      </div>
    </div>
  );

  if (state === "loading") return shell(
    <div className="flex flex-col items-center gap-3 py-10 text-slate-500">
      <Loader2 className="animate-spin" size={28} />
      <div className="text-sm">Loading offer…</div>
    </div>
  );

  if (state === "error") return shell(
    <div className="text-center py-6">
      <AlertTriangle size={42} className="text-amber-500 mx-auto mb-3" />
      <div className="text-lg font-semibold text-slate-900 mb-1">Offer unavailable</div>
      <div className="text-sm text-slate-500">{error}</div>
    </div>
  );

  const s = offer;
  const alreadyResponded = s?.status && s.status !== "pending";

  const successCard = (title, subtitle, color = "emerald") => (
    <div className="text-center py-4">
      {color === "emerald" ? <CheckCircle2 size={54} className="text-emerald-500 mx-auto mb-3" />
                           : <XCircle       size={54} className="text-slate-400   mx-auto mb-3" />}
      <div className="text-xl font-bold text-slate-900 mb-1">{title}</div>
      <div className="text-sm text-slate-500 leading-relaxed">{subtitle}</div>
    </div>
  );

  return shell(
    <>
      <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Open shift offer</div>
      <div className="text-lg font-bold text-slate-900 mb-1">Hi {s.recipient_first || s.recipient_name || "there"} 👋</div>
      <div className="text-sm text-slate-600 mb-4">Sent by {s.sent_by_name || "your manager"}</div>

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 mb-6">
        <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Shift</div>
        <div className="text-lg font-bold text-slate-900">{s.shift_label} <span className="text-slate-400 text-sm font-normal">({s.shift_code})</span></div>
        <div className="mt-2 flex items-center gap-4 text-sm text-slate-600">
          <span className="inline-flex items-center gap-1.5"><Calendar size={14} /> {s.date}</span>
          {s.shift_start && s.shift_end && (
            <span className="inline-flex items-center gap-1.5"><Clock size={14} /> {s.shift_start}–{s.shift_end}</span>
          )}
        </div>
      </div>

      {state === "submitting" && (
        <div className="flex items-center justify-center gap-2 text-slate-500 py-4">
          <Loader2 className="animate-spin" size={18} />
          <span className="text-sm">Sending your response…</span>
        </div>
      )}

      {state === "done" && result?.status === "accepted" && successCard(
        "You got the shift! 🎉",
        "Your schedule was updated automatically and your manager has been notified.",
      )}
      {state === "done" && result?.status === "denied" && successCard(
        "Thanks for letting us know",
        "No problem — we'll offer it to someone else.",
        "gray",
      )}
      {state === "done" && result?.status === "already_filled" && successCard(
        "Someone else grabbed it",
        "Sorry, this shift was already filled by another team member. Thanks for being quick!",
        "gray",
      )}

      {state === "ready" && !alreadyResponded && !action && (
        <div className="flex gap-2">
          <button data-testid="offer-accept"
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg py-3 font-semibold transition-colors"
            onClick={() => submit("accept")}>
            Accept shift
          </button>
          <button data-testid="offer-deny"
            className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg py-3 font-semibold transition-colors"
            onClick={() => submit("deny")}>
            Not this time
          </button>
        </div>
      )}
      {state === "ready" && alreadyResponded && successCard(
        s.status === "accepted" ? "You already accepted this shift" :
        s.status === "denied"   ? "You already declined this offer" :
                                  "This offer is no longer open",
        s.status === "accepted" ? "Check your schedule — it's on there." :
                                  "Thanks for responding.",
        s.status === "accepted" ? "emerald" : "gray",
      )}
    </>
  );
}
